import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { startTour } from "@/lib/appTour";
import { RateAppModal } from "@/components/RateAppModal";
import {
  Sparkles,
  LogOut,
  Crown,
  BarChart3,
  ShieldCheck,
  Gauge,
  Download,
  HelpCircle,
  Star,
} from "lucide-react";

export function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const access = user?.access;
  const isOwner = !!user?.is_owner;
  const [usage, setUsage] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return undefined;
    if (window.__BRIMI_INSTALL_READY__) setCanInstall(true);
    const onReady = () => setCanInstall(true);
    const onInstalled = () => setCanInstall(false);
    window.addEventListener("brimi-install-ready", onReady);
    window.addEventListener("brimi-installed", onInstalled);
    return () => {
      window.removeEventListener("brimi-install-ready", onReady);
      window.removeEventListener("brimi-installed", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (typeof window.__brimiTriggerInstall !== "function") return;
    await window.__brimiTriggerInstall();
  };

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    const fetchUsage = () => {
      api
        .get("/code/usage")
        .then((r) => {
          if (!cancelled) setUsage(r.data);
        })
        .catch(() => {
          /* swallow — quota badge is best-effort */
        });
    };
    fetchUsage();
    const id = setInterval(fetchUsage, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isOwner]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  let badgeText = null;
  let badgeColor = null;
  let badgeTestId = null;
  if (access) {
    if (access.status === "active") {
      badgeText = `${access.plan === "yearly" ? "Yearly" : "Monthly"} · Active`;
      badgeColor = "mint";
      badgeTestId = "header-active-badge";
    } else if (access.status === "trial") {
      badgeText = `Trial · ${access.days_left}d left`;
      badgeColor = "yellow";
      badgeTestId = "header-trial-badge";
    } else {
      badgeText = "Trial ended";
      badgeColor = "coral";
      badgeTestId = "header-expired-badge";
    }
  }

  const badgeStyle = {
    mint: "bg-[hsl(var(--brimi-mint))]/15 border-[hsl(var(--brimi-mint))]/40 text-foreground",
    yellow: "bg-[hsl(var(--brimi-yellow))]/25 border-[hsl(var(--brimi-yellow))]/50 text-foreground",
    coral: "bg-[hsl(var(--brimi-coral))]/15 border-[hsl(var(--brimi-coral))]/40 text-[hsl(var(--brimi-coral))]",
  }[badgeColor];

  // Owner-only JDoodle quota badge
  let quotaText = null;
  let quotaTone = "mint";
  if (usage && typeof usage.used === "number") {
    const used = usage.used;
    const limit = usage.limit || 200;
    quotaText = `Today: ${used}/${limit} runs`;
    if (used >= limit * 0.9) quotaTone = "coral";
    else if (used >= limit * 0.5) quotaTone = "yellow";
  }
  const quotaStyle = {
    mint: "bg-[hsl(var(--brimi-mint))]/15 border-[hsl(var(--brimi-mint))]/40 text-foreground",
    yellow: "bg-[hsl(var(--brimi-yellow))]/25 border-[hsl(var(--brimi-yellow))]/60 text-foreground",
    coral: "bg-[hsl(var(--brimi-coral))]/15 border-[hsl(var(--brimi-coral))]/50 text-[hsl(var(--brimi-coral))]",
  }[quotaTone];

  return (
    <>
      {/* Owner-mode global indicator — thin gradient strip + tint at the very top */}
      {isOwner && (
        <div
          data-testid="owner-mode-bar"
          className="sticky top-0 z-50 h-1 bg-gradient-to-r from-[hsl(var(--brimi-coral))] via-[hsl(var(--brimi-yellow))] to-[hsl(var(--brimi-mint))]"
        />
      )}
      <header
        data-testid="app-header"
        data-owner={isOwner ? "true" : "false"}
        className={`sticky ${isOwner ? "top-1" : "top-0"} z-40 backdrop-blur-xl border-b border-border/60 ${
          isOwner ? "bg-[hsl(var(--brimi-yellow))]/10" : "bg-white/75"
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <Link
            to={user ? "/subjects" : "/"}
            data-testid="brand-link"
            className="flex items-center gap-2 group shrink-0"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(var(--brimi-blue))] to-[hsl(var(--brimi-coral))] flex items-center justify-center shadow-sm group-hover:rotate-6 transition-transform">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-black text-xl tracking-tight">Brimi AI</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                ICSE Tutor
              </div>
            </div>
          </Link>

          {user && (
            <div className="flex items-center gap-2 sm:gap-3">
              {isOwner && (
                <div
                  data-testid="header-owner-badge"
                  title="You're signed in as a course owner — you can upload & manage chapter materials"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[hsl(var(--brimi-coral))] to-[hsl(var(--brimi-yellow))] text-white text-xs font-black uppercase tracking-wider shadow-sm"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Owner
                </div>
              )}
              {isOwner && (
                <div
                  data-testid="header-owner-badge-mobile"
                  title="Owner mode"
                  className="sm:hidden inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(var(--brimi-coral))] to-[hsl(var(--brimi-yellow))] text-white shadow-sm"
                >
                  <ShieldCheck className="w-4 h-4" />
                </div>
              )}
              {isOwner && quotaText && (
                <div
                  data-testid="header-jdoodle-quota"
                  title="JDoodle free-tier daily usage. Resets at midnight UTC."
                  className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${quotaStyle}`}
                >
                  <Gauge className="w-3.5 h-3.5" />
                  {quotaText}
                </div>
              )}
              {badgeText && (
                <div
                  data-testid={badgeTestId}
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${badgeStyle}`}
                >
                  {badgeText}
                </div>
              )}
              {access?.status !== "active" && (
                <Link
                  to="/pricing"
                  data-testid="header-upgrade-btn"
                  className="pill-btn bg-[hsl(var(--brimi-coral))] hover:bg-[hsl(var(--brimi-coral))]/90 text-white text-xs sm:text-sm px-3 sm:px-4 py-2"
                >
                  <Crown className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Upgrade</span>
                </Link>
              )}
              <Link
                to="/progress"
                data-testid="header-progress-btn"
                title="My progress"
                className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-xs sm:text-sm px-3 py-2"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Progress</span>
              </Link>
              {canInstall && (
                <button
                  type="button"
                  onClick={handleInstall}
                  data-testid="header-install-btn"
                  title="Install Brimi AI on your home screen"
                  className="pill-btn bg-[hsl(var(--brimi-yellow))] hover:bg-[hsl(var(--brimi-yellow))]/90 text-foreground text-xs sm:text-sm px-3 sm:px-4 py-2 shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden md:inline">Install app</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => startTour({ navigate })}
                data-testid="header-tour-btn"
                title="Take a 30-second tour"
                className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-sm px-3 py-2"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden lg:inline">Tour</span>
              </button>
              <button
                type="button"
                onClick={() => setRateOpen(true)}
                data-testid="header-rate-btn"
                title="Rate Brimi AI"
                className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-sm px-3 py-2"
              >
                <Star className="w-4 h-4" />
                <span className="hidden lg:inline">Rate</span>
              </button>
              <div
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/5"
                data-testid="user-name-badge"
              >
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--brimi-coral))] text-white text-xs font-bold flex items-center justify-center">
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <span className="text-sm font-semibold">{user.name}</span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-sm px-3 py-2"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </header>
      <RateAppModal
        open={rateOpen}
        onClose={() => setRateOpen(false)}
        user={user}
      />
    </>
  );
}
